import { CertificateVerification } from "./CertificateVerification";
import { CertificateVerificationResponseData } from "../../../models/CertificateQRCodeVerificationData";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

interface Props {
  certificateData: CertificateVerificationResponseData;
}

/**
 * @function useStyles
 * Custom styles for the component.
 */
const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    padding: "0.5rem 0",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      padding: "0.5rem 0",
    },
  },
  label: {
    whiteSpace: "nowrap",
    marginRight: "1rem",
    padding: "0.25rem 0.5rem",
    width: "10rem",
    [theme.breakpoints.down("xs")]: {
      marginRight: 0,
      marginBottom: "0.1rem",
      padding: "0.1rem 0.1rem",
      width: "auto",
    },
  },
  value: {
    flex: 1,
    wordBreak: "break-word",
    padding: "0.25rem 0.5rem",
    fontWeight: "bold",
    [theme.breakpoints.down("xs")]: {
      padding: "0.1rem 0.1rem",
    },
  },
}));

/**
 * Displays verified certificate data.
 *
 * @param certificateData - The verified certificate details
 */
export const CertificateVerificationData = ({ certificateData }: Props) => {
  const { t } = useTranslation(["certificate", "snackbars"]);

  const certificateDataItems = [
    {
      label: t("verification.success.name", { ns: "certificate" }),
      value: certificateData.fullname,
    },
    {
      label: t("verification.success.birthday", { ns: "certificate" }),
      value: certificateData.birth_date,
    },
    {
      label: t("verification.success.course", { ns: "certificate" }),
      value: certificateData.course,
    },
    {
      label: t("verification.success.issueDate", { ns: "certificate" }),
      value: certificateData.issue_date,
    },
    {
      label: t("verification.success.validUntil", { ns: "certificate" }),
      value: certificateData.validity_date ? certificateData.validity_date : "-",
    },
  ];

  const classes = useStyles();

  return (
    <CertificateVerification
      emoticon={certificateData.is_valid ? "✅" : "⚠️"}
      heading={
        certificateData.is_valid
          ? t("verification.success.validTitle", { ns: "certificate" })
          : t("verification.success.invalidTitle", { ns: "certificate" })
      }
    >
      <Box>
        {certificateDataItems.map(({ label, value }) => (
          <Box key={label} className={classes.row}>
            <Typography variant="body1" className={classes.label}>
              {label}:
            </Typography>
            <Typography variant="body1" className={classes.value}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </CertificateVerification>
  );
};
